import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from 'common/toast';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from 'reactstrap';
import { postGenerateBoostingNewsV2 } from 'store/thunks';
import * as Yup from "yup";

interface ModalGenerateBoostingNewsV2Props {
    isOpen: boolean;
    onRefresh?: () => void,
    onClose?: () => void,
}

const ModalGenerateBoostingNewsV2: React.FC<ModalGenerateBoostingNewsV2Props> = ({ isOpen, onRefresh, onClose }) => {
    const { t, i18n } = useTranslation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleClose = () => {
        onClose && onClose();
        formik.setValues({
            sources: [
                {
                    keyword: '',
                    url: '',
                },
            ],
        });
        formik.setErrors({});
    }

    const handleSubmit = async (values: any) => {
        try {
            if (!!isLoading) {
                return;
            }
            setIsLoading((_prev) => true);
            const inputs = (values?.sources || [])?.map((_s: any) => ({
                keyword: _s?.keyword || '',
                url: _s?.url || '',
            }));
            const res: any = await postGenerateBoostingNewsV2({ inputs });
            setIsLoading((_prev) => false);
            if (res?.code === 200) {
                toast(`${t('The process has been completed.')}`, CONFIG_OPTION_TOAST_NORMAL);
                onRefresh && onRefresh();
                formik.setValues({
                    sources: [
                        {
                            keyword: '',
                            url: '',
                        },
                    ],
                });
            } else {
                toast(`${res || ''}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        sources: Yup.array().of(Yup.object().shape({
            keyword: Yup.string().required(`${t("This is required")}`),
            url: Yup.string().required(`${t("This is required")}`),
        })).nullable(),
    });

    const formik = useFormik({
        initialValues: {
            sources: [
                {
                    keyword: '',
                    url: '',
                },
            ],
        },
        validationSchema: validationSchema,
        onSubmit: handleSubmit,
    });

    const handleAddSource = () => {
        formik.setFieldValue(`sources`, [...formik.values.sources, { keyword: '', url: '' }]);
    }
    const handleRemoveSource = (index: number) => {
        formik.setFieldValue(`sources`, formik.values.sources.filter((_: any, i: number) => i !== index));
    }

    useEffect(() => {
        if (isOpen) {
            formik.setErrors({});
        }
    }, [isOpen]);


    return (
        <>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="lg"
                scrollable={false}
            >
                <ModalHeader toggle={handleClose}>
                    <>{t("Button Generate Boosting News v2")}</>
                </ModalHeader>
                <ModalBody className="pt-2">
                    <form onSubmit={formik.handleSubmit}>
                        <div>
                            <div>
                                {
                                    (formik.values.sources || []).map((source: any, index: number) => (
                                        <Row key={`${index}`}>
                                            <Col sm={3} md={3} xl={3} className="mt-3">
                                                <Label htmlFor={`sources.${index}.keyword`} className="form-label">{t('Keyword')}<span className="text-danger"> *</span></Label>
                                                <Input
                                                    id={`sources.${index}.keyword`}
                                                    name={`sources.${index}.keyword`}
                                                    placeholder={`${t("Keyword")}...`}
                                                    className="form-control"
                                                    classNamePrefix="name-prefix"
                                                    value={formik.values.sources[index].keyword}
                                                    onChange={(e: any) => {
                                                        formik.setFieldValue(`sources[${index}].keyword`, e.target.value);
                                                    }}
                                                />
                                                {formik.touched.sources?.[index] && formik.errors.sources?.[index] ? (
                                                    <div className="text-danger mt-2">{(formik.errors.sources?.[index] as any)?.keyword}</div>
                                                ) : null}
                                            </Col>

                                            <Col sm={9} md={9} xl={9} className="mt-3">
                                                <Label htmlFor={`sources.${index}.url`} className="form-label">{t('URL')}<span className="text-danger"> *</span></Label>
                                                <div className="d-flex gap-4">
                                                    <Input
                                                        id={`sources.${index}.url`}
                                                        name={`sources.${index}.url`}
                                                        placeholder={`${t("URL")}...`}
                                                        className="form-control"
                                                        classNamePrefix="name-prefix"
                                                        value={formik.values.sources[index].url}
                                                        onChange={(e: any) => {
                                                            formik.setFieldValue(`sources[${index}].url`, e.target.value);
                                                        }}
                                                    />
                                                    <button
                                                        style={{ width: '100px' }}
                                                        type="button"
                                                        className="btn btn-soft-danger"
                                                        disabled={formik.values.sources.length === 1}
                                                        onClick={() => handleRemoveSource(index)}
                                                    >
                                                        <i className="ri-subtract-line align-bottom me-1"></i>
                                                        {t('Remove')}
                                                    </button>
                                                </div>

                                                {formik.touched.sources?.[index] && formik.errors.sources?.[index] ? (
                                                    <div className="text-danger mt-2">{(formik.errors.sources?.[index] as any)?.url}</div>
                                                ) : null}
                                            </Col>
                                        </Row>
                                    ))
                                }

                                <Row>
                                    <Col sm={12} md={12} xl={12} className='mt-4 mb-3 text-start'>
                                        <button
                                            style={{ width: '80px' }}
                                            type="button"
                                            className="btn btn-soft-success"
                                            onClick={handleAddSource}
                                        >
                                            <i className="ri-add-line align-bottom me-1"></i>
                                            {t('Button Add')}
                                        </button>
                                    </Col>
                                </Row>
                            </div>

                            <Row>
                                <Col sm={12} md={12} xl={12} className='mb-3 text-end d-flex gap-3 justify-content-end'>
                                    <button
                                        type="button"
                                        disabled={isLoading}
                                        className="btn btn-secondary"
                                        onClick={handleClose}
                                    >
                                        {t('Button Close')}
                                    </button>
                                    <button
                                        style={{ width: i18n?.language === 'en' ? '240px' : '180px' }}
                                        type="submit"
                                        disabled={isLoading}
                                        className="btn btn-primary">
                                        {isLoading ? <Spinner size="sm me-2" ></Spinner> : <></>}
                                        {t('Button Generate Boosting News v2')}
                                    </button>
                                </Col>
                            </Row>
                        </div>
                    </form>
                </ModalBody>
            </Modal>
        </>
    );
};

export default React.memo(ModalGenerateBoostingNewsV2);
